// Driver dashboard overview: next vehicle check countdown, open shift count
// (nudges if over the recommended 2-at-a-time, since unclosed shifts are
// part of what's let vehicles run over their RUC limit -- see
// src/lib/vehicleOdometer.ts), Mondays only, this week's still-open jobs --
// the same "Open Jobs" definition ClosedCheck.tsx uses admin-side, just
// scoped to this one driver -- and, below all of that, any admin-posted
// notices (see Settings -> Driver Notices). This is a nudge, not an
// enforced limit -- nothing here blocks a driver from starting another
// shift.
import React, { useEffect, useState } from "react";
import { ClipboardCheck, Clock3, CalendarClock, Megaphone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDriverVehicleCheckStatus } from "@/hooks/useDriverVehicleCheckStatus";
import { getOpenShiftCount, getOpenJobsThisWeekCount } from "@/lib/driverNotifications";
import { loadActiveNoticeBoardMessages, noticeBoardTitle, type NoticeBoardMessage } from "@/lib/driverNoticeBoard";

const MAX_RECOMMENDED_OPEN_SHIFTS = 2;

// 0 = Sunday .. 6 = Saturday. Matches the Mon-Sun payroll week the open-jobs
// count itself is scoped to (driverNotifications.ts) -- Monday is the day
// after that week closes off.
const RECONCILIATION_DOW = 1;

type Tone = "red" | "amber" | "green" | "blue";

const TONE_CLASSES: Record<Tone, string> = {
  red: "text-red-300 border-red-700/40 bg-red-950/25",
  amber: "text-amber-300 border-amber-700/40 bg-amber-950/25",
  green: "text-green-300 border-green-700/40 bg-green-950/25",
  blue: "text-blue-300 border-blue-700/40 bg-blue-950/25",
};

const Tile: React.FC<{
  title: string;
  value: string;
  sub?: string;
  tone: Tone;
  icon: React.ElementType;
}> = ({ title, value, sub, tone, icon: Icon }) => (
  <div className={`rounded-2xl border p-4 ${TONE_CLASSES[tone]}`}>
    <div className="flex items-center gap-2 text-sm font-medium opacity-90">
      <Icon className="w-4 h-4" />
      {title}
    </div>
    <div className="text-4xl font-bold mt-2">{value}</div>
    {sub && <div className="text-xs opacity-80 mt-1">{sub}</div>}
  </div>
);

// Same tile shell as the stat tiles above, but a click target rather than a
// number -- heading + "Click to read", the full message only shows once
// opened (see the Dialog below).
const NoticeTile: React.FC<{ notice: NoticeBoardMessage; onClick: () => void }> = ({ notice, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left rounded-2xl border p-4 transition-colors hover:bg-yellow-950/40 ${TONE_CLASSES.amber}`}
  >
    <div className="flex items-center gap-2 text-sm font-medium opacity-90">
      <Megaphone className="w-4 h-4" />
      Notice
    </div>
    <div className="text-base font-semibold mt-2 line-clamp-2">{noticeBoardTitle(notice)}</div>
    <div className="text-xs opacity-80 mt-1">Click to read</div>
  </button>
);

interface DriverNotificationsProps {
  driverName: string;
}

const DriverNotifications: React.FC<DriverNotificationsProps> = ({ driverName }) => {
  const checkStatus = useDriverVehicleCheckStatus(driverName);
  const [openShifts, setOpenShifts] = useState<number | null>(null);
  const [openJobs, setOpenJobs] = useState<number | null>(null);
  const [notices, setNotices] = useState<NoticeBoardMessage[]>([]);
  const [openNotice, setOpenNotice] = useState<NoticeBoardMessage | null>(null);
  const [loading, setLoading] = useState(true);

  const isMonday = new Date().getDay() === RECONCILIATION_DOW;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const [shifts, jobs, board] = await Promise.all([
        getOpenShiftCount(driverName),
        isMonday ? getOpenJobsThisWeekCount(driverName) : Promise.resolve(null),
        loadActiveNoticeBoardMessages(),
      ]);
      if (!cancelled) {
        setOpenShifts(shifts);
        setOpenJobs(jobs);
        setNotices(board);
        setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverName]);

  const worst = checkStatus.worst;
  const daysUntilDue =
    worst?.daysSinceLastCheck != null ? Math.max(0, worst.intervalDays - worst.daysSinceLastCheck) : null;

  const shiftCount = openShifts ?? 0;
  const overShiftLimit = shiftCount > MAX_RECOMMENDED_OPEN_SHIFTS;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
        <p className="text-sm text-muted-foreground">
          Your at-a-glance overview -- vehicle check, open shifts, and (on Mondays) any jobs still needing to be
          closed off.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {checkStatus.loading ? (
          <div className="rounded-2xl border border-border p-4 text-sm text-muted-foreground">
            Loading vehicle check status...
          </div>
        ) : worst ? (
          <Tile
            title="Next Vehicle Check"
            value={checkStatus.isLockedOut ? "Overdue" : `${daysUntilDue}d`}
            sub={
              checkStatus.isLockedOut
                ? "Complete a check to unlock the rest of your tabs"
                : `Due within ${worst.intervalDays} days of your last check`
            }
            tone={checkStatus.isLockedOut ? "red" : daysUntilDue !== null && daysUntilDue <= 1 ? "amber" : "green"}
            icon={ClipboardCheck}
          />
        ) : (
          <Tile
            title="Next Vehicle Check"
            value="—"
            sub="No inspection on record yet"
            tone="blue"
            icon={ClipboardCheck}
          />
        )}

        <Tile
          title="Open Shifts"
          value={loading ? "…" : String(shiftCount)}
          sub={
            overShiftLimit
              ? `Please close some down -- no more than ${MAX_RECOMMENDED_OPEN_SHIFTS} at a time keeps vehicle KM tracking accurate`
              : shiftCount > 0
              ? "Remember to close these out"
              : "All shifts closed"
          }
          tone={overShiftLimit ? "red" : shiftCount > 0 ? "amber" : "green"}
          icon={Clock3}
        />

        {isMonday && (
          <Tile
            title="Open Jobs This Week"
            value={loading ? "…" : String(openJobs ?? 0)}
            sub={(openJobs ?? 0) > 0 ? "Jobs not yet marked complete for this week" : "All jobs closed for this week"}
            tone={(openJobs ?? 0) > 0 ? "amber" : "green"}
            icon={CalendarClock}
          />
        )}
      </div>

      {notices.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {notices.map((n) => (
            <NoticeTile key={n.id} notice={n} onClick={() => setOpenNotice(n)} />
          ))}
        </div>
      )}

      <Dialog open={!!openNotice} onOpenChange={(v) => !v && setOpenNotice(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-yellow-400" />
              {openNotice ? noticeBoardTitle(openNotice) : ""}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground whitespace-pre-wrap">{openNotice?.message}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverNotifications;
